<template>
  <div id="list-boards" class="list-boards">
    <h2 class="page-heading sr-only">List Boards</h2>
    <i v-if="loading" class="loader">Loading&hellip;</i>
    <div v-else>
      <section class="filter-section">
        <button type="button" @click="onToggleFilters" class="button button--as-link toggle-filters-btn">{{ toggleFiltersButtonText }}</button>
        <div class="filters-wrapper">
          <transition name="fade">
            <div v-if="showingFilters">
              <tabs @changed="filterTabChanged" :options="{useUrlFragment: false}" class="filter-tabs">
                <tab :name="tabNameNoFilter">
                  <p>No filter</p>
                </tab>
                <tab :name="tabNameByMode">
                  <div class="form-row select">
                    <label for="mode" class="sr-only">Mode</label>
                    <select id="mode" v-model="mode" required>
                      <option v-for="item in boardModesFilterSelectData" :value="item.value" :key="item.value">
                        {{ item.label }}
                      </option>
                    </select>
                    <div class="select__arrow"></div>
                  </div>
                </tab>
                <tab :name="tabNameByPromoted">
                  <div class="form-row select">
                    <label for="promoted" class="sr-only">Promoted</label>
                    <select id="promoted" v-model="promoted" required>
                      <option v-for="item in promotedSelectData" :value="item.value" :key="item.value">
                        {{ item.label }}
                      </option>
                    </select>
                    <div class="select__arrow"></div>
                  </div>
                </tab>
                <tab :name="tabNameByCategory">
                  <div class="form-row select">
                    <label for="category" class="sr-only">Category</label>
                    <select id="category" v-model="category" required>
                      <option v-for="item in categorySelectData" :value="item.CONSTANT" :key="item.CONSTANT">
                        {{ item.DISPLAY_NAME }}
                      </option>
                    </select>
                    <div class="select__arrow"></div>
                  </div>
                </tab>
                <tab :name="tabNameBySubstring">
                  <div class="form-row select">
                    <label for="substring" class="sr-only">Substring</label>
                    <input id="substring" type="text" v-model="filterSubstring" placeholder="Enter substring ..." />
                  </div>
                </tab>
                <tab :name="tabNameByKeyword">
                  <div class="form-row select">
                    <label for="keyword" class="sr-only">Substring</label>
                    <input id="keyword" type="text" list="keywordsDataList" v-model="filterKeyword" placeholder="Enter keyword ..." />
                    <datalist id="keywordsDataList">
                      <option v-for="keyword in allKeywords" :value="keyword" :key="keyword" />
                    </datalist>
                  </div>
                </tab>
                <tab :name="tabNameById">
                  <div class="form-row select">
                    <label for="id" class="sr-only">ID</label>
                    <input id="id" type="text" list="idDataList" v-model="filterId" placeholder="Enter ID ..." />
                    <datalist id="idDataList">
                      <option v-for="id in allIDs" :value="id" :key="id" />
                    </datalist>
                  </div>
                </tab>
                <tab :name="tabNameByLayout">
                  <div class="form-row select">
                    <label for="layout" class="sr-only">Layout</label>
                    <select id="layout" v-model="layout" required>
                      <option v-for="item in layoutSelectData" :value="item.value" :key="item.value">
                        {{ item.label }}
                      </option>
                    </select>
                    <div class="select__arrow"></div>
                  </div>
                </tab>
              </tabs>
            </div>
          </transition>
        </div>
      </section>
      <p class="count-display">
        <span v-if="filterBy !== 'NO_FILTER'">Filtered boards: {{ this.filteredBoards.length }}, </span>Total boards: {{ this.getBoards.length }}
      </p>
      <div v-if="filteredBoards.length > 0" class="form-row select form-row--orderby">
        <div class="form-row__child">
          <label for="orderby">Order by</label>
          <select name="orderby" id="orderby" v-model="orderBy">
            <option v-for="item in orderBySelectData" :value="item.value" :key="item.value">
              {{ item.label }}
            </option>
          </select>
          <div class="select__arrow"></div>
        </div>
        <div class="form-row__child">
          <label for="orderbyDirection" class="sr-only">Direction</label>
          <select name="orderbyDirection" id="orderbyDirection" v-model="orderByDirection">
            <option v-for="item in orderByDirectionSelectData" :value="item.value" :key="item.value">
              {{ item.label }}
            </option>
          </select>
          <div class="select__arrow"></div>
        </div>
      </div>
      <ul v-if="filteredBoards.length > 0" class="boards-list style-none">
        <li v-for="(board, index) in filteredBoards" :key="board.boardId">
          <ListBoardsItem
            :boardId="board.boardId"
            :boardPortraitLayout="board.boardPortraitLayout"
            :category="board.category"
            :createdAt="board.boardCreatedAt"
            :index="index"
            :mode="board.mode"
            :name="board.name"
            :orderBy="orderBy"
            :promoted="board.promoted"
            :slug="board.slug"
          />
        </li>
      </ul>
      <p v-else class="no-boards-message">No boards to display after filtering</p>
      <router-link v-if="userCanCreate" to="/new-board" title="Create a new board" class="new-board-btn">
        <span class="sr-only">Create a new board</span>
        <span class="material-icons" aria-hidden="true">add_circle</span>
      </router-link>
    </div>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import uniq from 'lodash/uniq';
import sortBy from 'lodash/sortBy';
import db from './../firebaseInit';
import ListBoardsItem from './../components/ListBoardsItem';
import {
  BOARD_MODES_FILTER_SELECT_DATA,
  BOARD_MODES,
  BOARD_PORTRAIT_LAYOUTS_SELECT_DATA,
  CATEGORIES,
  DEFAULT_BOARD_PORTRAIT_LAYOUT,
  FILTER_BOARDS_BY,
  NOTIFICATION_SETTINGS,
  PROMOTED_SELECT_DATA,
  PROMOTED,
  ORDER_BY,
  ORDER_BY_SELECT_DATA,
  ORDER_BY_DIRECTION,
  ORDER_BY_DIRECTION_SELECT_DATA,
} from './../constants/globalConstants';

const boardsVuexModule = createNamespacedHelpers('boardsVuexModule');
const userVuexModule = createNamespacedHelpers('userVuexModule');

export default {
  name: 'list-boards',
  title: 'List Boards',
  components: {
    ListBoardsItem,
  },
  data() {
    return {
      allIDs: [],
      allKeywords: [],
      boardModesFilterSelectData: BOARD_MODES_FILTER_SELECT_DATA,
      category: sortBy(CATEGORIES, 'DISPLAY_NAME')[0].CONSTANT,
      categorySelectData: sortBy(CATEGORIES, 'DISPLAY_NAME'),
      filterBy: FILTER_BOARDS_BY.NO_FILTER.CONSTANT,
      filterId: '',
      filterKeyword: '',
      filterSubstring: '',
      layout: DEFAULT_BOARD_PORTRAIT_LAYOUT,
      mode: BOARD_MODES.LIVE.CONSTANT,
      orderBy: ORDER_BY.CREATION_DATE.CONSTANT,
      orderByDirection: ORDER_BY_DIRECTION.DESCENDING.CONSTANT,
      orderByDirectionSelectData: ORDER_BY_DIRECTION_SELECT_DATA,
      orderBySelectData: ORDER_BY_SELECT_DATA,
      promoted: PROMOTED.PROMOTED.SELECT_VALUE,
      promotedSelectData: PROMOTED_SELECT_DATA,
      showingFilters: false,
      tabNameByLayout: FILTER_BOARDS_BY.BY_LAYOUT.DISPLAY_NAME,
      tabNameByCategory: FILTER_BOARDS_BY.BY_CATEGORY.DISPLAY_NAME,
      tabNameById: FILTER_BOARDS_BY.BY_ID.DISPLAY_NAME,
      tabNameByKeyword: FILTER_BOARDS_BY.BY_KEYWORD.DISPLAY_NAME,
      tabNameByMode: FILTER_BOARDS_BY.BY_MODE.DISPLAY_NAME,
      tabNameByPromoted: FILTER_BOARDS_BY.BY_PROMOTED.DISPLAY_NAME,
      tabNameBySubstring: FILTER_BOARDS_BY.BY_SUBSTRING.DISPLAY_NAME,
      tabNameNoFilter: FILTER_BOARDS_BY.NO_FILTER.DISPLAY_NAME,
    };
  },
  created() {
    window.scrollTo(0, 0);
    this.getBoards.forEach(board => {
      this.allIDs.push(board.boardId);
      this.allKeywords = this.allKeywords.concat(board.keywords);
    });
    this.allIDs = this.allIDs.sort();
    this.allKeywords = uniq(this.allKeywords).sort();
  },
  methods: {
    onToggleFilters: function() {
      this.showingFilters = !this.showingFilters;
      if (!this.showingFilters) this.filterBy = FILTER_BOARDS_BY.NO_FILTER.CONSTANT;
    },
    filterTabChanged(selectedTab) {
      switch (selectedTab.tab.name) {
        case FILTER_BOARDS_BY.NO_FILTER.DISPLAY_NAME:
          this.filterBy = FILTER_BOARDS_BY.NO_FILTER.CONSTANT;
          break;
        case FILTER_BOARDS_BY.BY_MODE.DISPLAY_NAME:
          this.filterBy = FILTER_BOARDS_BY.BY_MODE.CONSTANT;
          break;
        case FILTER_BOARDS_BY.BY_PROMOTED.DISPLAY_NAME:
          this.filterBy = FILTER_BOARDS_BY.BY_PROMOTED.CONSTANT;
          break;
        case FILTER_BOARDS_BY.BY_CATEGORY.DISPLAY_NAME:
          this.filterBy = FILTER_BOARDS_BY.BY_CATEGORY.CONSTANT;
          break;
        case FILTER_BOARDS_BY.BY_SUBSTRING.DISPLAY_NAME:
          this.filterBy = FILTER_BOARDS_BY.BY_SUBSTRING.CONSTANT;
          break;
        case FILTER_BOARDS_BY.BY_KEYWORD.DISPLAY_NAME:
          this.filterBy = FILTER_BOARDS_BY.BY_KEYWORD.CONSTANT;
          break;
        case FILTER_BOARDS_BY.BY_ID.DISPLAY_NAME:
          this.filterBy = FILTER_BOARDS_BY.BY_ID.CONSTANT;
          break;
        case FILTER_BOARDS_BY.BY_LAYOUT.DISPLAY_NAME:
          this.filterBy = FILTER_BOARDS_BY.BY_LAYOUT.CONSTANT;
          break;
        default:
          this.$snotify.error('Tab name not recognised', '', NOTIFICATION_SETTINGS);
      }
    },
  },
  computed: {
    ...boardsVuexModule.mapGetters(['getBoards']),
    ...userVuexModule.mapGetters(['userCanRead', 'userCanEdit', 'userCanCreate', 'userCanDelete', 'userCanViewAdminStuff']),
    loading: function() {
      return this.getBoards.length === 0;
    },
    toggleFiltersButtonText: function() {
      return this.showingFilters ? 'Hide filters' : 'Show filters';
    },
    layoutSelectData: function() {
      return BOARD_PORTRAIT_LAYOUTS_SELECT_DATA.filter(layout => this.usedLayouts.includes(layout.value));
    },
    filteredBoards: function() {
      let tempArray = [];
      switch (this.filterBy) {
        case FILTER_BOARDS_BY.NO_FILTER.CONSTANT:
          tempArray = this.getBoards;
          break;
        case FILTER_BOARDS_BY.BY_MODE.CONSTANT:
          if (this.mode === BOARD_MODES.LIVE_AND_TESTING.CONSTANT) {
            tempArray = this.getBoards.filter(board => board.mode === BOARD_MODES.LIVE.CONSTANT || board.mode === BOARD_MODES.TESTING.CONSTANT);
          } else {
            tempArray = this.getBoards.filter(board => board.mode === this.mode);
          }
          break;
        case FILTER_BOARDS_BY.BY_PROMOTED.CONSTANT:
          tempArray = this.getBoards.filter(board => board.promoted === this.promoted);
          break;
        case FILTER_BOARDS_BY.BY_CATEGORY.CONSTANT:
          tempArray = this.getBoards.filter(board => board.category === this.category);
          break;
        case FILTER_BOARDS_BY.BY_SUBSTRING.CONSTANT:
          tempArray = this.getBoards.filter(board => board.name.toLowerCase().includes(this.filterSubstring.toLowerCase()));
          break;
        case FILTER_BOARDS_BY.BY_KEYWORD.CONSTANT:
          tempArray = this.getBoards.filter(board => board.keywords.includes(this.filterKeyword) || this.filterKeyword === '');
          break;
        case FILTER_BOARDS_BY.BY_ID.CONSTANT:
          tempArray = this.getBoards.filter(board => board.boardId === this.filterId || this.filterId === '');
          break;
        case FILTER_BOARDS_BY.BY_LAYOUT.CONSTANT:
          tempArray = this.getBoards.filter(board => board.boardPortraitLayout === this.layout);
          break;
        default:
          tempArray = this.getBoards;
      }
      tempArray = tempArray.map(item => {
        item.boardCreatedAtSeconds = item.boardCreatedAt.seconds;
        return item;
      });
      const orderByProperty = ORDER_BY[this.orderBy].PROPERTY;
      tempArray = sortBy(tempArray, orderByProperty);
      if (this.orderByDirection === ORDER_BY_DIRECTION.ASCENDING.CONSTANT) {
        return tempArray;
      } else {
        return tempArray.reverse();
      }
    },
    usedLayouts: function() {
      return uniq(this.getBoards.map(board => board.boardPortraitLayout)).sort();
    },
  },
};
</script>

<style lang="scss" scoped>
.list-boards {
  margin-bottom: 11rem;
}

.boards-list {
  border: 1px solid var(--color-subtle-border);
  border-radius: var(--border-radius);
  overflow: hidden;
  margin: 0 auto;
  max-width: var(--max-width-main-content);
}

.new-board-btn {
  bottom: 1.5rem;
  color: var(--bg-color-inverted);
  display: block;
  line-height: 1;
  position: fixed;
  right: 1.5rem;
  text-decoration: none;

  &::before {
    content: '';
    display: block;
    background-color: var(--color-list-boards-new-board-btn-outer-circle-bg);
    width: 11rem;
    height: 11rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    z-index: 1;
  }

  .material-icons {
    font-size: 9rem;
    position: relative;
    z-index: 2;
  }
}

.filter-section {
  margin: 0 auto 3rem auto;
  max-width: var(--max-width-main-content);
}

.filters-wrapper {
  overflow: hidden;
  padding-top: 1rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s, transform 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
  transform: translateY(-5rem);
}

.count-display {
  color: var(--text-color-alpha-50);
  max-width: none;
  text-align: center;
}

.form-row {
  &--orderby {
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin: 0 auto 1rem auto;
    max-width: var(--max-width-main-content);

    label {
      flex-shrink: 0;
      margin-right: 1rem;
    }
    select {
      max-width: 20rem;
    }
  }

  &__child {
    align-items: center;
    display: flex;
    margin-right: 1rem;
    position: relative;

    &:last-child {
      margin-right: 0;
    }
  }
}

.no-boards-message {
  margin: 6rem auto 0 auto;
  max-width: var(--max-width-main-content);
  text-align: center;
}

.loader {
  display: block;
  left: 50%;
  margin-top: calc(50vh - 4rem);
  position: absolute;
  top: 0;
  transform: translateX(-50%);
}
</style>

<style lang="scss">
.filter-tabs {
  ul.tabs-component-tabs {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    overflow-y: auto;
  }

  li.tabs-component-tab {
    border-top-left-radius: var(--border-radius);
    border-top-right-radius: var(--border-radius);
    width: 100%;
    &.is-active {
      background-color: var(--color-active-tab-background);

      a {
        font-weight: 700;
      }
    }
  }

  a.tabs-component-tab-a {
    display: block;
    padding: 1rem;
    min-width: 13rem;
    text-align: center;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .tabs-component-panels {
    background-color: var(--color-active-tab-background);
    border-bottom-left-radius: var(--border-radius);
    border-bottom-right-radius: var(--border-radius);
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 8rem;
    padding: 2rem;

    p {
      margin: 0;
      max-width: none;
    }
  }

  .tabs-component-panel {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;

    select {
      min-width: 22rem;
    }
  }
}
</style>
